import { AvailableCategories, ProductCategoriesLink } from "components/Navbar/types"
import { PageLayout } from "components/PageLayout"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Outlet, useLocation, useNavigate } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { setProductsCategories, setProductsCategoriesLoading } from "store/product/productSlice"
import { selectUserInfo } from "store/user/userSlice"
import { IntegrationType } from "types"
import { Box } from "@strapi/design-system"

export const AuthorizedUserLayout: React.FC = () => {
  const navigate = useNavigate()
  const { username, integrationType, userExpired, merchantId, groups } =
    useAppSelector(selectUserInfo)
  const productsCategories = useAppSelector((state) => state.products.productsCategories)
  const location = useLocation()
  const { t } = useTranslation()
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const service = getTintVTOService()
  const dispatch = useAppDispatch()

  const setCategories = useCallback(async () => {
    dispatch(setProductsCategoriesLoading(true))
    let categories = []

    if (groups.includes("Admin")) {
      categories = AvailableCategories.filter((category) => category.enabled).map(({ name }) => ({
        name,
      }))
    } else {
      // Guard clause: don't fetch merchant config if merchantId is empty
      if (!merchantId) {
        console.warn("merchantId is empty, skipping merchant config fetch")
        dispatch(setProductsCategoriesLoading(false))
        return
      }

      const merchantConfig = await service.getMerchantCdnConfig(merchantId)
      categories =
        merchantConfig?.categories
          ?.filter((el) =>
            AvailableCategories.some((cat) => cat.name === el.toUpperCase() && cat.enabled)
          )
          ?.map((el) => ({ name: el.toUpperCase() })) ?? []
    }

    dispatch(
      setProductsCategories({
        categories,
        t,
      })
    )
    dispatch(setProductsCategoriesLoading(false))
  }, [dispatch, groups, merchantId, service, t])

  const checkAuth = useCallback(() => {
    if (!username && integrationType === IntegrationType.COMMON) navigate("/login")
    else if (userExpired) navigate("/login/integrations/re-enter")
    else if (username && ["/login", "/login/integrations/re-enter"].includes(location.pathname))
      if (productsCategories.length > 0) navigate(productsCategories[0].to, { replace: true })
      else navigate(ProductCategoriesLink.MAKEUP, { replace: true })

    setIsCheckingAuth(false)
  }, [username, integrationType, userExpired, location.pathname, navigate, productsCategories])

  useEffect(checkAuth, [location.pathname, navigate, checkAuth])

  useEffect(() => {
    void setCategories()
  }, [setCategories, username, integrationType, userExpired, merchantId])

  if (isCheckingAuth)
    return (
      <Box
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      />
    )

  return (
    <PageLayout>
      <Outlet />
    </PageLayout>
  )
}
